import {useMutation, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {usePagedList, type PagedListParams} from '@/hooks/common/use-paged-list'
import type {components} from '@/lib/api-schema'

export type CuarentenaDocumento = components['schemas']['CuarentenaRead']
export type CuarentenaApertura = components['schemas']['AperturaRead']
export type CuarentenaPedido = components['schemas']['PedidoRead']

export const CUARENTENA_PAGE_SIZE = 20

// Distinta de la cuarentena de hojas de la batería (`hooks/bateria/use-cuarentena.ts`).
function base(businessId: string | undefined) {
  return `/businesses/${businessId}/cuarentena`
}

/** Solo el custodio (`require_custodio_salud`): al dueño y a modo dios el backend les da 403. */
export function useCuarentena(businessId: string | undefined, params: PagedListParams) {
  return usePagedList<CuarentenaDocumento>(
    ['cuarentena', businessId],
    businessId ? base(businessId) : undefined,
    params,
    CUARENTENA_PAGE_SIZE
  )
}

/** Registra la apertura antes de firmar la URL: por eso es un POST y no un enlace directo. */
export function useAbrirCuarentena(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (documentoId: string) =>
      (await api.post<CuarentenaApertura>(`${base(businessId)}/${documentoId}/abrir`, {})).data,
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['cuarentena', businessId]}),
  })
}

/** Convierte el documento en evidencia normal y cancela el reloj de supresión. */
export function useSoltarCuarentena(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {documentoId: string; motivo: string}) =>
      (await api.post<CuarentenaDocumento>(`${base(businessId)}/${input.documentoId}/soltar`, {motivo: input.motivo}))
        .data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['cuarentena', businessId]})
      queryClient.invalidateQueries({queryKey: ['sgsst-evidence', businessId]})
    },
  })
}

/** No suelta nada: el documento sigue retenido y su reloj sigue corriendo. */
export function usePedirSinHistoria(businessId: string) {
  return useMutation({
    mutationFn: async (documentoId: string) =>
      (await api.post<CuarentenaPedido>(`${base(businessId)}/${documentoId}/pedir-sin-historia`, {})).data,
  })
}
